import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import * as turf from "@turf/turf";
import _ from "lodash";
import moment from "moment-timezone";
import { toDegMinSec } from "@/utils/GeoUtil";
import { secToTime } from "@/utils/DateUtil";
import * as MapUtil from "@/utils/MapUtil";
import { parseComma, toPlusMinusSignString } from "@/utils/NumberUtil";
const fuelConsumptionRepository = AiseaApiFactory.get("fuelConsumption");
const vesselRepository = AiseaApiFactory.get("enterpriseVessel");
const vesselOriginsRepository = AiseaApiFactory.get("enterpriseVesselOrigins");
const VesselDisplayChannelRepository = AiseaApiFactory.get(
  "enterpriseVesselDisplayChannel"
);
const SDFRepository = AiseaApiFactory.get(
  "enterpriseVesselSecureDataflowChannel"
);

export default {
  // 航海記録の期間を取得する
  async fetchVoyagePeriod({ _ }, vesselId) {
    const res = await fuelConsumptionRepository.getVoyages(vesselId, {
      limit: 9999,
    });
    if (!res || !res.status) {
      throw { error: true, msg: "航海一覧の取得に失敗しました。" };
    }
    if (res.data.fuel_consumption_report_voyage_history_indices.length === 0) {
      throw { error: true, msg: "航海が見つかりませんでした。" };
    }

    const lastIndex =
      res.data.fuel_consumption_report_voyage_history_indices.length - 1;

    const startDate =
      res.data.fuel_consumption_report_voyage_history_indices[lastIndex]
        .start_time;

    const endDate =
      res.data.fuel_consumption_report_voyage_history_indices[0].start_time;

    return {
      startDate: startDate,
      endDate: endDate,
    };
  },

  async fetchAllVoyageList({ _ }, vesselId) {
    const res = await fuelConsumptionRepository.getVoyages(vesselId, {
      limit: 9999,
    });
    if (!res || !res.status) {
      throw { error: true, msg: "航海一覧の取得に失敗しました。" };
    }
    if (res.data.fuel_consumption_report_voyage_history_indices.length === 0) {
      throw { error: true, msg: "航海が見つかりませんでした。" };
    }
    return res.data.fuel_consumption_report_voyage_history_indices;
  },

  async fetchCsv({ _ }, payload) {
    const param = {
      start_at: payload.from,
      end_at: payload.to,
    };
    const res = await fuelConsumptionRepository.getCsv(payload.vesselId, param);
    if (!res || !res.status) {
      throw { error: true, msg: "航行中燃料消費量CSVの取得に失敗しました。" };
    }

    return res.data;
  },

  async fetchCsvAnchor({ _ }, payload) {
    const param = {
      start_at: payload.from,
      end_at: payload.to,
    };
    const res = await fuelConsumptionRepository.getCsvAtAnchor(
      payload.vesselId,
      param
    );
    if (!res || !res.status) {
      throw { error: true, msg: "停泊中消費燃料の取得に失敗しました。" };
    }

    return res.data;
  },

  //期間別の省エネ解析レポートを取得
  async fetchVoyageRecordsPeriod({ dispatch, commit }, payload) {
    const periods = {
      start_at: payload.startAt,
      end_at: payload.endAt,
      limit: 9999,
    };
    const res = await fuelConsumptionRepository.getSummaryPeriod(
      payload.vesselId,
      periods
    );

    if (!res || !res.status) {
      throw "error";
    }

    // 推奨航路参照率
    if (res.data.route_agreement_rate === null) {
      commit("SET_ROUTE_AGREEMENT_RATE", "-");
    } else {
      commit(
        "SET_ROUTE_AGREEMENT_RATE",
        Math.round(res.data.route_agreement_rate * 100)
      );
    }

    // WRの結果テーブル作成
    let weatherRoutingResults = [];
    commit("SET_ACTUAL_ROUTE", res.data.actual_route_aggregated_data);
    commit(
      "SET_ACTUAL_ROUTE_EXISTS_RECOMMENDED_AGGREGATED_DATA",
      res.data.actual_route_exists_recommended_aggregated_data
    );

    let actualRouteGrandTotal = await dispatch("_createRouteTotalData", {
      id: 0,
      itemName: "実績航路合計",
      routeData: res.data.actual_route_aggregated_data,
    });
    weatherRoutingResults.push(actualRouteGrandTotal);

    let actualRouteTotalData = await dispatch("_createRouteTotalData", {
      id: 1,
      itemName: "WR実績航路",
      routeData: res.data.actual_route_exists_recommended_aggregated_data,
    });
    weatherRoutingResults.push(actualRouteTotalData);

    let recommendedDiffData = await dispatch("_createRouteDiffData", {
      id: 2,
      itemName: "推薦航路",
      actualRouteAggregated: res.data.actual_route_aggregated_data,
      aggregatedData: res.data.recommended_route_aggregated_data,
      actualRouteExists:
        res.data.actual_route_exists_recommended_aggregated_data,
    });
    weatherRoutingResults.push(recommendedDiffData);

    let usualRoutedDiffData = await dispatch("_createRouteDiffData", {
      id: 3,
      itemName: "計画航路",
      actualRouteAggregated: res.data.actual_route_aggregated_data,
      aggregatedData: res.data.usual_route_aggregated_data,
      actualRouteExists:
        res.data.actual_route_exists_recommended_aggregated_data,
    });
    weatherRoutingResults.push(usualRoutedDiffData);
    commit("SET_WEATHER_ROUTING_RESULTS_DATA", weatherRoutingResults);

    // 航路の変針点
    commit("SET_USUAL_ROUTE_WAYPOINTS", res.data.usual_route_waypoints);
    commit(
      "SET_RECOMENDED_ROUTE_WAYPOINTS",
      res.data.recommended_route_waypoints
    );
    commit("SET_ACTUAL_ROUTE_WAYPOINTS", res.data.actual_routes_waypoints);

    // チャートデータ
    commit("SET_CHART_DATA", res.data.actual_route_aggregated_data_for_graph);

    // 主機稼働率
    commit("SET_OPERATION_RATE", res.data.operation_rate);
  },

  _createRouteTotalData({ _ }, args) {
    let routeData = args.routeData;

    let accumulatedDistance = "-";
    if (routeData.accumulated_distance) {
      accumulatedDistance = parseComma(routeData.accumulated_distance, 0);
    }

    let accumulatedTotalUnderwayTime = secToTime(0);
    if (routeData.accumulated_seconds) {
      accumulatedTotalUnderwayTime = secToTime(
        routeData.accumulated_seconds,
        0
      );
    }

    let averageSpeedThroughWater = "-";
    if (routeData.average_speed_through_water) {
      averageSpeedThroughWater = parseComma(
        routeData.average_speed_through_water,
        2
      );
    }

    let mainEngineFuelConsumption = "-";
    if (routeData.main_engine_fuel_consumption) {
      mainEngineFuelConsumption = parseComma(
        routeData.main_engine_fuel_consumption,
        1
      );
    }

    let mainEngineFuelConsumptionPerNm = "-";
    if (
      routeData.main_engine_fuel_consumption &&
      routeData.accumulated_distance
    ) {
      mainEngineFuelConsumptionPerNm = parseComma(
        routeData.main_engine_fuel_consumption / routeData.accumulated_distance,
        1
      );
    }

    let mainEngineFuelConsumptionPerHour = "-";
    if (
      routeData.main_engine_fuel_consumption &&
      routeData.accumulated_seconds
    ) {
      mainEngineFuelConsumptionPerHour = parseComma(
        routeData.main_engine_fuel_consumption /
          (routeData.accumulated_seconds / 3600),
        1
      );
    }

    return {
      id: args.id,
      itemName: args.itemName,
      accumulatedDistance: accumulatedDistance,
      accumulatedTotalUnderwayTime: accumulatedTotalUnderwayTime,
      averageSpeedThroughWater: averageSpeedThroughWater,
      mainEngineFuelConsumption: mainEngineFuelConsumption,
      mainEngineFuelConsumptionPerNm: mainEngineFuelConsumptionPerNm,
      mainEngineFuelConsumptionPerHour: mainEngineFuelConsumptionPerHour,
    };
  },

  _createRouteDiffData({ _ }, args) {
    let agg = args.aggregatedData;
    let ace = args.actualRouteExists;

    let accumulatedDistanceDiff = "-";
    if (agg.accumulated_distance) {
      accumulatedDistanceDiff = parseComma(agg.accumulated_distance, 0);
    }

    let accumulatedSecondsDiff = secToTime(0);
    if (agg.accumulated_seconds) {
      accumulatedSecondsDiff = secToTime(agg.accumulated_seconds, 0);
    }

    let averageSpeedThroughWater = "-";
    if (agg.average_speed_through_water) {
      averageSpeedThroughWater = agg.average_speed_through_water.toFixed(2);
    }

    let mainEngineFuelConsumptionDiff = "-";
    if (agg.main_engine_fuel_consumption) {
      let diff =
        ace.main_engine_fuel_consumption - agg.main_engine_fuel_consumption;
      mainEngineFuelConsumptionDiff = toPlusMinusSignString(diff.toFixed(2));
    }

    let mainEngineFuelConsumptionPerNm = "-";
    if (agg.main_engine_fuel_consumption && agg.accumulated_distance) {
      let ma = ace.main_engine_fuel_consumption / ace.accumulated_distance;
      let mb = agg.main_engine_fuel_consumption / agg.accumulated_distance;
      let diff = ma - mb;
      mainEngineFuelConsumptionPerNm = toPlusMinusSignString(diff.toFixed(1));
    }

    let mainEngineFuelConsumptionPerHour = "-";
    if (agg.main_engine_fuel_consumption && agg.accumulated_seconds) {
      let ra =
        ace.main_engine_fuel_consumption / (ace.accumulated_seconds / 3600);
      let rb =
        agg.main_engine_fuel_consumption / (agg.accumulated_seconds / 3600);
      let diff = ra - rb;
      mainEngineFuelConsumptionPerHour = toPlusMinusSignString(diff.toFixed(1));
    }

    return {
      id: args.id,
      itemName: args.itemName,
      accumulatedDistance: accumulatedDistanceDiff,
      accumulatedTotalUnderwayTime: accumulatedSecondsDiff,
      averageSpeedThroughWater: averageSpeedThroughWater,
      mainEngineFuelConsumption: mainEngineFuelConsumptionDiff,
      mainEngineFuelConsumptionPerNm: mainEngineFuelConsumptionPerNm,
      mainEngineFuelConsumptionPerHour: mainEngineFuelConsumptionPerHour,
    };
  },

  //航路別の省エネ解析レポートを取得
  async fetchVoyageRecords({ dispatch, commit }, payload) {
    const res = await fuelConsumptionRepository.getSummaryRecord(
      payload.vesselId,
      payload.historyId
    );

    if (!res || !res.status) {
      throw "error";
    }

    let wrFeatures = [];
    if (res.data.usual_route_waypoints !== null) {
      let usualRouteFeature = await dispatch("_createRouteFeature", {
        points: res.data.usual_route_waypoints,
        routeType: "usual",
        routeColor: "#3BD37B",
        startFeatureId: 200,
      });
      wrFeatures = wrFeatures.concat(usualRouteFeature);
    }

    if (res.data.recommended_route_waypoints !== null) {
      let recommendedRouteFeature = await dispatch("_createRouteFeature", {
        points: res.data.recommended_route_waypoints,
        routeType: "recommended",
        routeColor: "#E27070",
        startFeatureId: 300,
      });
      wrFeatures = wrFeatures.concat(recommendedRouteFeature);
    }

    commit("SET_ROUTE_FEATURE_COLLECTION", turf.featureCollection(wrFeatures));
    commit("SET_START_PORT", res.data.start_port);
    commit("SET_GOAL_PORT", res.data.goal_port);
    commit("SET_USUAL_ROUTE", res.data.usual_route_aggregated_data);
    commit("SET_RECOMENDED_ROUTE", res.data.recommended_route_aggregated_data);
    commit("SET_ACTUAL_ROUTE_WAYPOINTS", res.data.actual_route_waypoints);
    commit("SET_ACTUAL_ROUTE", res.data.actual_route_aggregated_data);
    commit("SET_ROUTE_AGREEMENT_RATE", res.data.route_agreement_rate);
    commit("SET_CHART_DATA", res.data.actual_route_aggregated_data_for_graph);
    commit("SET_OPERATION_RATE", res.data.operation_rate);
    commit(
      "SET_ACTUAL_ROUTE_EXISTS_RECOMMENDED_AGGREGATED_DATA",
      res.data.actual_route_exists_recommended_aggregated_data
    );

    dispatch("_createActualRouteGeoJson", {
      routeColor: "#1BA9E9",
      points: _.cloneDeep(res.data.actual_route_waypoints),
      startPort: res.data.start_port,
      goalPort: res.data.goal_port,
      actualRouteData: res.data.actual_route_aggregated_data,
      hasWR: res.data.recommended_route_waypoints !== null,
    });

    dispatch(
      "_createSectionLines",
      _.cloneDeep(res.data.actual_route_waypoints)
    );
  },

  _createRouteFeature({ _ }, payload) {
    let features = [];
    let coordinatesList = [];
    let featureId = payload.startFeatureId;
    let lastFeatureId = payload.startFeatureId + payload.points.length;
    payload.points.forEach(el => {
      featureId++;
      let coord = [el[1], el[0]];
      coordinatesList.push(coord);
      let title = payload.routeType === "recommended" ? "推奨航路" : "計画航路";
      let estimatedArrivalDateTime = moment
        .unix(el[2])
        .format("YYYY-MM-DD HH:mm");
      let wayPointFeature = turf.point(
        coord,
        {
          pointType: payload.routeType,
          pointColor: payload.routeColor,
          isGoalPoint: featureId === lastFeatureId,
          popupHtml: `<div><p class="headLine" style="color: ${
            payload.routeColor
          }">${title}</p><p>到着予定日時：${estimatedArrivalDateTime}</p><p>緯度：N${toDegMinSec(
            coord[1]
          )}</p><p>経度：E${toDegMinSec(coord[0])}</p></div>`,
        },
        { id: featureId }
      );
      features = features.concat(wayPointFeature);
    });

    let lineFeature = turf.lineString(coordinatesList, {
      routeType: payload.routeType,
      routeColor: payload.routeColor,
    });
    features = features.concat(lineFeature);

    return features;
  },

  _createActualRouteGeoJson(
    { commit },
    { routeColor, points, startPort, goalPort, actualRouteData, hasWR }
  ) {
    let coordinatesList = [];
    let beforePt;
    let beforePtTime;
    let features = [];
    let missingLineFeatures = [];
    let ptList = [];

    points.forEach(el => {
      let currentTime = moment.unix(el[2]);
      let coordinates = [el[1], el[0]];
      coordinatesList.push(coordinates);
      let currentPt = turf.point(coordinates, { time: el[2] });
      if (!beforePt) {
        ptList.push(currentPt);
        beforePtTime = currentTime;
        beforePt = currentPt;
        return;
      }

      let diff = currentTime.diff(beforePtTime, "minutes");
      if (diff >= 10) {
        if (ptList.length >= 2) {
          let coord = [];
          ptList.forEach(p => {
            coord.push(turf.getCoord(p));
          });
          let linestring = turf.lineString(coord, { isMissingLine: false });
          features.push(linestring);
        }

        let misLinestring = turf.lineString(
          [turf.getCoord(beforePt), turf.getCoord(currentPt)],
          { isMissingLine: true }
        );
        missingLineFeatures.push(misLinestring);

        ptList = [];
        ptList.push(currentPt);
      } else {
        ptList.push(currentPt);
      }

      beforePtTime = currentTime;
      beforePt = currentPt;
    });

    let coord = [];
    ptList.forEach(p => {
      coord.push(turf.getCoord(p));
    });

    let linestring = turf.lineString(coord, { isMissingLine: false });
    features.push(linestring);
    features = features.concat(missingLineFeatures);

    let ptFeatures = turf.points(coordinatesList);
    let startPortName = startPort ? startPort.name : "-";
    let startGeo = turf.getGeom(ptFeatures.features[0]);
    let startTime = actualRouteData.start_time;
    let startFeature = turf.feature(
      startGeo,
      {
        pointType: "start",
        popupHtml: `<p class="headLine"  style="color:${routeColor}">実績航路</p><p>出発港：${startPortName}</p><p>出発時間：${moment(
          startTime
        ).format("YYYY-MM-DD HH:mm")}</p><p>緯度：N${toDegMinSec(
          startGeo.coordinates[1]
        )}</p><p>経度：E${toDegMinSec(
          startGeo.coordinates[0]
        )}</p> <p>距離：0 nm</p>`,
      },
      { id: 101 }
    );
    features = features.concat(startFeature);

    let goalPortName = goalPort ? goalPort.name : "-";
    let goalGeo = turf.getGeom(ptFeatures.features.slice(-1)[0]);
    let goalTime = actualRouteData.goal_time;
    let voyageDistance = actualRouteData.accumulated_distance.toFixed(2);
    let goalFeature = turf.feature(
      goalGeo,
      {
        pointType: "goal",
        popupHtml: `<p class="headLine"  style="color:${routeColor}">実績航路</p><p>目的港： ${goalPortName}</p><p>到着時間：${moment(
          goalTime
        ).format("YYYY-MM-DD HH:mm")}</p><p>緯度： N${toDegMinSec(
          goalGeo.coordinates[1]
        )}</p><p>経度： E${toDegMinSec(
          goalGeo.coordinates[0]
        )}</p> <p>距離：${voyageDistance} nm</p>`,
      },
      { id: 102 }
    );
    commit("SET_ACTUAL_ROUTE_LINES_FEATURES", features.concat(goalFeature));

    // Zoomレベルの算出
    let endPoints = turf.getCoords(goalFeature);
    let startPoints = turf.getCoords(startFeature);
    let centerPoint = turf.getCoords(turf.center(ptFeatures));
    let lat = centerPoint[1];
    let distanceToEnd = turf.distance(startPoints, endPoints) * 1000;
    let distanceToCenter = turf.distance(startPoints, centerPoint) * 1000;
    let distance = distanceToEnd;
    let adjustZoomLevel = hasWR ? 0 : -1;
    if (distanceToEnd < distanceToCenter) {
      distance = distanceToCenter;
      adjustZoomLevel = -2;
    }
    let zoom = MapUtil.calculateZoomLevel(lat, distance, adjustZoomLevel);
    commit("SET_MAP_INITIAL_POSITION", { center: centerPoint, zoom: zoom });
  },

  _createSectionLines({ commit }, points) {
    let startTime = moment
      .unix(points[0][2])
      .startOf("hour")
      .unix();
    let after1h = startTime + 3600;
    let endTime = moment
      .unix(points.slice(-1)[0][2])
      .startOf("hour")
      .unix();
    let features = [];
    let coordinates = [];
    let format = "YYYY-MM-DD HH:mm";
    points.push([null, null, endTime + 3600, null]);
    let i = 0;
    points.forEach(el => {
      if (
        moment
          .unix(el[2])
          .isBetween(moment.unix(startTime), moment.unix(after1h))
      ) {
        coordinates.push([el[1], el[0]]);
      } else {
        let properties = {
          timeRange: `${moment.unix(startTime).format(format)} - ${moment
            .unix(after1h)
            .format(format)}`,
          hasCoordinates: true,
        };
        if (coordinates.length >= 2) {
          let linestring = turf.lineString(coordinates, properties);
          features.push(linestring);
        } else {
          properties.hasCoordinates = false;
          if (el[1] !== null) {
            let point = turf.point([el[1], el[0]], properties);
            features.push(point);
          }
        }
        coordinates = [];
        startTime = startTime + 3600;
        after1h = startTime + 3600;
      }
    });
    commit("SET_SECTION_LINES_FEATURES", features);
  },

  setGraphSyncLine({ commit, getters }, index) {
    let syncLine = getters.getGraphSyncLine(index);
    commit("SET_CURRENT_GRAPH_SYNC_LINE", syncLine || []);
  },

  async fetchVoyageList({ commit }, payload) {
    const page = payload.page ? payload.page : 0;
    const limit = payload.limit ? payload.limit : 50;
    const params = {
      limit: limit,
      page: page,
    };
    const res = await fuelConsumptionRepository.getVoyages(
      payload.vesselId,
      params
    );
    if (!res || !res.status) {
      commit("SET_VOYAGE_TOTAL_PAGE", 0);
      commit("SET_VOYAGE_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "航海一覧の取得に失敗しました。" };
    }
    if (res.data.fuel_consumption_report_voyage_history_indices === 0) {
      commit("SET_VOYAGE_TOTAL_PAGE", 0);
      commit("SET_VOYAGE_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "航海が見つかりませんでした。" };
    }
    const voyages = res.data.fuel_consumption_report_voyage_history_indices;
    commit("SET_VOYAGES", voyages);
    commit("SET_VOYAGE_TOTAL_PAGE", res.data.pageable.total_pages);
    commit("SET_VOYAGE_TOTAL_ELEMENTS", res.data.pageable.total_elements);
  },

  async fetchAllVessels({ commit }) {
    const res = await vesselRepository.search({ limit: 9999, page: 0 });
    if (!res || !res.status) {
      commit("SET_VESSEL_TOTAL_PAGE", 0);
      commit("SET_VESSEL_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "船舶一覧の取得に失敗しました。" };
    }
    if (res.data.vessels.length === 0) {
      commit("SET_VESSEL_TOTAL_PAGE", 0);
      commit("SET_VESSEL_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "船舶が見つかりませんでした。" };
    }
    const vessels = res.data.vessels.map(item => {
      const vesselName = item.name_ja ? item.name_ja : item.name;
      return {
        id: item.id,
        name: vesselName,
        pictures: item.pictures,
        enterpriseVesselId: item.enterprise_vessel_id,
      };
    });

    commit("SET_VESSELS", vessels);
    commit("SET_VESSEL_TOTAL_PAGE", res.data.pageable.total_pages);
    commit("SET_VESSEL_TOTAL_ELEMENTS", res.data.pageable.total_elements);
  },

  async fetchVesselList({ commit }, page) {
    if (!page) {
      page = 0;
    }
    const res = await vesselRepository.search({ limit: 50, page: page });
    if (!res || !res.status) {
      commit("SET_VESSEL_TOTAL_PAGE", 0);
      commit("SET_VESSEL_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "船舶一覧の取得に失敗しました。" };
    }
    if (res.data.vessels.length === 0) {
      commit("SET_VESSEL_TOTAL_PAGE", 0);
      commit("SET_VESSEL_TOTAL_ELEMENTS", 0);
      throw { error: true, msg: "船舶が見つかりませんでした。" };
    }
    const vessels = res.data.vessels.map(item => {
      const vesselName = item.name_ja ? item.name_ja : item.name;
      return {
        id: item.id,
        name: vesselName,
        pictures: item.pictures,
        enterpriseVesselId: item.enterprise_vessel_id,
      };
    });

    commit("SET_VESSELS", vessels);
    commit("SET_VESSEL_TOTAL_PAGE", res.data.pageable.total_pages);
    commit("SET_VESSEL_TOTAL_ELEMENTS", res.data.pageable.total_elements);
  },

  async fetchSensorData({ commit }, payload) {
    let columnDefs = [];
    let rowData = [];

    // オリジン番号指定で取得
    const resOrigins = await vesselOriginsRepository.getOrigins(
      payload.vessel.id,
      {
        origin_number: 2, // バルコムのID
      }
    );
    if (resOrigins.data.error_code !== undefined) {
      return;
    }

    // オリジンに紐づくチャンネルバージョンを全て取得
    const vesselOriginId = resOrigins.data.id;
    const resChannelVersions = await vesselOriginsRepository.getChannelVersions(
      payload.vessel.id,
      vesselOriginId
    );
    if (resChannelVersions.data.error_code !== undefined) {
      return;
    }

    let targetChannelVersions = resChannelVersions.data[0];
    if (resChannelVersions.data.length >= 2) {
      // 日付降順でソート
      const sortChannelVersions = resChannelVersions.data.sort(
        (a, b) => Date.parse(b.started_at) - Date.parse(a.started_at)
      );
      for (const channelVersion of sortChannelVersions) {
        // 未来日は無視
        if (moment(channelVersion.started_at).isAfter(moment().utc())) {
          continue;
        }
        targetChannelVersions = channelVersion;
        break;
      }
    }

    // チャンネル発生元を取得
    const channelVersionId = targetChannelVersions.id;
    const resDisplayChannel = await VesselDisplayChannelRepository.getChannelOriginsUnique(
      payload.vessel.enterpriseVesselId,
      {
        channel_version_id: channelVersionId,
      }
    );

    if (resDisplayChannel.data.error_code !== undefined) {
      return;
    }

    const channelOriginId = resDisplayChannel.data.id;
    const resChannelGroups = await VesselDisplayChannelRepository.getChannelGroups(
      payload.vessel.enterpriseVesselId,
      channelOriginId
    );

    if (resChannelGroups.data.error_code !== undefined) {
      return;
    }

    let channelGroups = resChannelGroups.data.channel_groups;

    for (const channel of channelGroups) {
      let params = { version_id: channelVersionId };
      let channelGroupId = channel.channel_group_id;
      let resChannels = await VesselDisplayChannelRepository.getChannels(
        payload.vessel.enterpriseVesselId,
        channelOriginId,
        channelGroupId,
        params
      );

      let channelList = resChannels.data.channels;
      let channelNames = [];
      let channelIds = [];
      for (const channel of channelList) {
        channelIds.push(channel.channel_id);
        channelNames.push(`${channel.channel_name} (${channel.unit})`);
      }
      columnDefs.push({
        sensorName: channel.name,
        channelIds: channelIds,
        channelNames: channelNames.sort(),
      });
    }

    let sensorValues = [];
    for (const columnDef of columnDefs) {
      let tSensor = {};
      for (const channelId of columnDef.channelIds) {
        let resHourlyChannelData = await SDFRepository.getHourlyChannelData(
          payload.vessel.id,
          channelId,
          payload.timeRange
        );

        for (const hcData of resHourlyChannelData.data) {
          if (!(hcData.time in tSensor)) {
            tSensor[hcData.time] = {};
          }
          // 温度
          if (channelId.includes("TEMP")) {
            tSensor[hcData.time].temperature =
              hcData.value_number === 0 ? "-" : hcData.value_number.toFixed(2);
          } else {
            // 圧力
            tSensor[hcData.time].pressure =
              hcData.value_number === 0
                ? "-"
                : hcData.value_number / Math.pow(10, 6); // パスカルをメガに変換
          }
          tSensor[hcData.time].sensorName = columnDef.sensorName;
        }
      }
      sensorValues.push(tSensor);
    }

    for (const sensorValue of sensorValues) {
      Object.keys(sensorValue).forEach(datetime => {
        let sensor = {
          sensorName: sensorValue[datetime].sensorName,
          temperature: sensorValue[datetime].temperature,
          pressure: sensorValue[datetime].pressure,
        };
        let re = rowData.find(el => el.datetime === datetime);
        if (re === undefined) {
          let data = {
            datetime: datetime,
            datetimeJST: moment(datetime)
              .tz("Asia/Tokyo")
              .format("YYYY/MM/DD HH:mm"),
            sensors: [sensor],
          };
          rowData.push(data);
        } else {
          re.sensors.push(sensor);
        }
      });
    }

    commit("SET_SENSOR_COLUMN", columnDefs);
    commit("SET_SENSOR_ROW_DATA", rowData);
  },
};
